import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, KpiCard, SectionCard, EmptyState } from "../components/Common";
import { Archive, Search, Download, Calendar, User as UserIcon, Printer } from "lucide-react";
import { AdmissionDetail } from "./admitted/AdmissionDetail";
import { DISCHARGE_CONDITIONS } from "./admitted/parts";

function downloadCsv(filename, rows) {
  const header = ["Admission #", "Patient", "Patient ID", "Ward", "Bed", "Admitted", "Discharged", "LOS (days)", "Condition", "Discharge diagnosis", "Discharging doctor"];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.map(esc).join(",")];
  rows.forEach((r) => {
    const admittedAt = r.admitted_at ? new Date(r.admitted_at) : null;
    const dischargedAt = r.discharged_at ? new Date(r.discharged_at) : null;
    const los = admittedAt && dischargedAt ? Math.max(1, Math.round((dischargedAt - admittedAt) / 86400000)) : "";
    lines.push([
      r.admission_no, r.patient_name, r.patient_ths_id, r.ward, r.bed_no,
      admittedAt ? admittedAt.toISOString().slice(0, 10) : "",
      dischargedAt ? dischargedAt.toISOString().slice(0, 10) : "",
      los,
      r.discharge?.condition || "",
      r.discharge?.discharge_diagnosis || "",
      r.discharge?.discharged_by || r.discharged_by || "",
    ].map(esc).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
}

export default function Discharges() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [condition, setCondition] = useState("All");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admissions?status=Discharged");
      setItems(data || []);
    } catch { toast.error("Failed to load discharges"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
    return items.filter((a) => {
      const dAt = a.discharged_at ? new Date(a.discharged_at).getTime() : 0;
      if (from && dAt < from) return false;
      if (to && dAt > to) return false;
      if (condition !== "All" && (a.discharge?.condition || "") !== condition) return false;
      if (!q) return true;
      return [a.patient_name, a.patient_ths_id, a.admission_no, a.discharge?.discharge_diagnosis, a.admitting_diagnosis, a.discharge?.discharged_by, a.ward]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, query, dateFrom, dateTo, condition]);

  const conditionCounts = useMemo(() => {
    const c = {};
    DISCHARGE_CONDITIONS.forEach((k) => { c[k] = 0; });
    filtered.forEach((a) => {
      const k = a.discharge?.condition;
      if (k && c[k] !== undefined) c[k]++;
    });
    return c;
  }, [filtered]);

  if (selectedId) return <AdmissionDetail admissionId={selectedId} onBack={() => setSelectedId(null)} onChanged={load} />;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Discharges Archive"
        subtitle="Search past discharges by date, patient, or condition. Signed summaries are read-only; addenda still supported from the record view."
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid="disc-print-pack"
              onClick={() => {
                const q = new URLSearchParams();
                if (dateFrom) q.set("date_from", dateFrom);
                if (dateTo) q.set("date_to", dateTo);
                if (condition !== "All") q.set("condition", condition);
                window.open(`/print/discharges?${q.toString()}`, "_blank");
              }}
              disabled={filtered.length === 0}
              className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
            ><Printer className="w-4 h-4" /> Print Pack ({filtered.length})</button>
            <button
              data-testid="disc-export-csv"
              onClick={() => downloadCsv(`discharges_${new Date().toISOString().slice(0,10)}.csv`, filtered)}
              disabled={filtered.length === 0}
              className="h-10 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#1565C0] disabled:opacity-50"
            ><Download className="w-4 h-4" /> Export CSV ({filtered.length})</button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-disc-total" label="Discharges in view" value={filtered.length} icon={Archive} tone="blue" />
        <KpiCard testId="kpi-disc-improved" label="Improved" value={conditionCounts["Improved"]} icon={UserIcon} tone="green" />
        <KpiCard testId="kpi-disc-stable" label="Stable" value={conditionCounts["Stable"]} icon={UserIcon} tone="slate" />
        <KpiCard testId="kpi-disc-deceased" label="Deceased" value={conditionCounts["Deceased"]} icon={UserIcon} tone="red" />
      </div>

      <SectionCard
        title={`Discharge records (${filtered.length})`}
        testId="discharges-list-card"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="disc-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Patient, Patient ID, diagnosis, doctor…" className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
            </div>
            <div className="flex items-center gap-1 text-slate-500 text-xs"><Calendar className="w-4 h-4" /></div>
            <input type="date" data-testid="disc-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
            <span className="text-xs text-slate-400">→</span>
            <input type="date" data-testid="disc-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
            <select data-testid="disc-condition-filter" value={condition} onChange={(e) => setCondition(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="All">All conditions</option>
              {DISCHARGE_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        }
      >
        {loading ? (
          <div className="p-6 text-slate-500 text-sm" data-testid="disc-list-loading">Loading discharges…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Archive} title="No discharges match" description="Adjust the date range or filters." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((a) => {
              const admittedAt = a.admitted_at ? new Date(a.admitted_at) : null;
              const dischargedAt = a.discharged_at ? new Date(a.discharged_at) : null;
              const los = admittedAt && dischargedAt ? Math.max(1, Math.round((dischargedAt - admittedAt) / 86400000)) : null;
              return (
                <li key={a.admission_id}>
                  <button data-testid={`disc-row-${a.admission_id}`} onClick={() => setSelectedId(a.admission_id)} className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{a.patient_name}</span>
                        <span className="text-xs text-slate-500 tabular">{a.patient_ths_id}</span>
                        <span className="text-xs text-slate-400">·</span>
                        <span className="text-xs text-slate-500">{a.admission_no}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {a.discharge?.discharge_diagnosis || a.admitting_diagnosis} · {a.ward} · {dischargedAt ? dischargedAt.toLocaleDateString() : "—"}{los ? ` · LOS ${los}d` : ""} · by {a.discharge?.discharged_by || a.discharged_by || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {a.discharge?.condition && <span className="chip chip-slate !inline-flex">{a.discharge.condition}</span>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
